import React, { useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { ChevronDownIcon } from '../../montefiore/icons/chevron-down';
import { MEAC_ORG_KEY } from './header-data';

export interface FlyoutMenuLink {
  text: string;
  link?: { href: string };
  showIcon: boolean;
}

interface HeaderSecondaryMenuProps {
  headline: FlyoutMenuLink;
  headlineSubtitle?: string;
  flyoutMenu: FlyoutMenuLink[];
}

export const HeaderSecondaryMenu: React.FC<HeaderSecondaryMenuProps> = ({
  headline,
  headlineSubtitle,
  flyoutMenu,
}: HeaderSecondaryMenuProps) => {
  const menuId = useId();
  const { t } = useTranslation(MEAC_ORG_KEY);
  const analytics = useAnalytics();

  const trackLink = useCallback(
    (clickText: string) => {
      analytics.navigationClickEvent({
        navigationType: 'top-bar',
        clickText,
        linkType: 'link',
      });
    },
    [analytics],
  );

  return (
    <div className={'lg:relative lg:block z-10 hidden'}>
      <div
        className={'mf-flyout'}
        data-analytics-event={'e_navigationClick'}
        data-analytics-navigation-type={'primary nav'}
      >
        <div className={'mf-flyout__overlay'} style={{ display: 'none' }} />
        <div className={'relative z-10 border-b border-gray-200 bg-white'}>
          <div className={'mf-wrap'}>
            <nav
              aria-label={t('aria.mainNavigation')}
              className={
                'items-strech lg:min-h-[5rem] flex min-h-[4rem] justify-between'
              }
            >
              <div
                className={
                  'md:max-w-[36%] flex h-full max-w-full shrink-0 flex-col justify-center self-center py-[0.5rem] pr-[5.1rem]'
                }
              >
                <a
                  onClick={() => {
                    trackLink(t(headline.text));
                  }}
                  className={
                    'mf-link mf-link--site-montefiore mf-link--primary mf-link--base mf-link--direction-backwards mf-link--animation-reverse mf-text-body-1-serif block font-[400]'
                  }
                  href={headline.link?.href}
                  data-analytics-click-text={headline.text}
                  data-analytics-link-type={'link'}
                >
                  <span className={'mf-link__text'}>
                    <span
                      className={'mf-link__text__inner whitespace-pre-line'}
                    >
                      {t(headline.text)}
                    </span>
                  </span>
                </a>
                {headlineSubtitle ? (
                  <p className={'mf-text-body-4-serif text-gray-500'}>
                    {t(headlineSubtitle)}
                  </p>
                ) : null}
              </div>
              <div className={'lg:flex lg:items-center hidden'}>
                <ul className={'mf-flyout__nav font-sans'}>
                  {flyoutMenu.map((flyoutItem, index) => (
                    <li className={'mf-flyout__nav__item'} key={index}>
                      <a
                        onClick={() => {
                          trackLink(t(flyoutItem.text));
                        }}
                        href={flyoutItem.link?.href}
                        className={'mf-flyout__nav__link'}
                        data-dropdown-index={index}
                        id={`${menuId}-dropdown-button-${index}`}
                        data-analytics-event={'e_navigationOpen'}
                        data-analytics-navigation-type={'primary'}
                        data-analytics-click-text={flyoutItem.text}
                      >
                        <span className={'pointer-events-none'}>
                          {t(flyoutItem.text)}
                        </span>
                        {flyoutItem.showIcon ? (
                          <span
                            className={
                              'mf-icon mf-icon--small mf-icon--primary pointer-events-none'
                            }
                            data-analytics-click-text={'chevron-down'}
                            data-analytics-link-type={'icon'}
                          >
                            <ChevronDownIcon
                              className={'mf-icon__svg'}
                              aria-hidden={'true'}
                              focusable={'false'}
                            />
                          </span>
                        ) : null}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};
